
// Base.
import Component from 'vue-class-component'
import Vue from 'vue'
import {Watch} from 'vue-property-decorator'
import {State} from 'vuex-class'

// Used Models.
import {User} from '../models/user'
import GridHeader from '../models/gridHeader'

// Used Components.
import AssignService from '../components/Users/AssignService.vue'
import CsvExport from '../components/Users/CsvExport.vue'
import ChangeEmail from '../components/ChangeEmail.vue'
import UsersChangeStatus from '@/components/Users/UsersChangeStatus.vue'
import DisplayActivationKey from '../components/Users/DisplayActivationKey.vue'
import GenerateLabels from '../components/Users/GenerateLabels.vue'
import ResetSinglePassword from '../components/Users/ResetSinglePassword.vue'
import ResetPassword from '../components/Users/ResetPassword.vue'
import EnableWifi from '../components/Users/EnableWifi.vue'
import HelpTooltip from '../components/Atomic/HelpTooltip.vue'
import CardTitle from '../components/Atomic/CardTitle.vue'

import Establishment from '@/models/establishment'
import SearchField from '@/components/Atomic/SearchField.vue'
import RedButton from '@/components/Atomic/RedButton.vue'

// Used for notifications.
import {INotificationService} from '../services/NotificationService'
import UserService from '@/services/UserService'
import ErrorHandlerService from '@/services/ErrorHandlerService'

import Permissions from '../models/constants/Permissions'
import BlueButton from '@/components/Atomic/BlueButton.vue'

import { sortStringValues } from '../utils/utils'
import CardTable from '@/components/Layout/CardTable.vue'
import {User as Storeuser} from '../store/index'
import moment from 'moment'

@Component({
  components: {
    CardTable,
    BlueButton,
    AssignService,
    CsvExport,
    ChangeEmail,
    UsersChangeStatus,
    DisplayActivationKey,
    GenerateLabels,
    ResetPassword,
    ResetSinglePassword,
    EnableWifi,
    CardTitle,
    SearchField,
    RedButton,
    HelpTooltip,
  },
})
export default class Users extends Vue {
  public $refs!: {
    usersChangeStatusDialog: UsersChangeStatus,
  }
  @State private user!: Storeuser

  // User properties.
  private users: User[] = []
  private filteredUsers: User[] = []
  private selectedUsers: User[] = []

  // UI properties.
  private headers: GridHeader[] = []
  private establishments: Establishment[] = []
  private functions: string[] = []
  private years: string[] = []
  private classes: string[] = []
  private wifiStatus: string[] = ['Activé', 'Désactivé']
  private adStatus: string[] = ['Activé', 'Désactivé']
  private page: number = 1
  private isPending: boolean = true
  private dialog: boolean = false
  private changeEmailDialog: boolean = false
  private basicSearch: string = ''

  private changeUserEmail!: User

  // Filter properties.
  private selectedEstablishmentId: string = ''
  private selectedSchoolName: string = ''
  private selectedYears: string[] = []
  private selectedClasses: string[] = []
  private selectedFunctions: string[] = []
  private selectedWifiStatus: string = ''
  private selectedADEnabled: string = ''
  private bus: any

  // Used to handle errors
  private errorHandlerService: ErrorHandlerService

  // Used for notification.
  private $notificationService!: INotificationService

  constructor() {
    super()
    this.errorHandlerService = new ErrorHandlerService()
  }

  public mounted() {
    this.initialize()
  }

  public async generateLabels(users: User[], firstComment: string, secondComment: string) {
    try {
      this.isPending = true
      const data = {accounts: users, firstComment, secondComment, schoolId: this.selectedEstablishmentId}
      this.$notificationService.loading(this.$t('USER.GenerateLabels').toString())
      const response = await UserService.generateStudentsLabel(data)
      const url = URL.createObjectURL(response)
      const a = document.createElement('a')
      const date = moment().format('YY-MM-DD')
      document.body.appendChild(a)
      a.setAttribute('style', 'display: none;')
      a.href = url
      a.download = date + '-etiquettes'
      a.click()
      window.URL.revokeObjectURL(url)
      this.$notificationService.snotify.clear()
      this.$notificationService.success(this.$t('USER.GenerateLabelsSuccess').toString())
    } catch (error: any) {
      this.$notificationService.snotify.clear()
      this.errorHandlerService.handle(this, error.response.data.code, true)
    } finally {
      this.isPending = false
    }
    // window.open
  }

  private capitalize(value: string) {
    var trimmed_value = value.trimStart().trimEnd()
    return trimmed_value.charAt(0).toUpperCase() + trimmed_value.slice(1).toLowerCase()
  }

  private get displayIsLarge() {
    return this.$vuetify.breakpoint.lgAndUp
  }

  private get hasAccessToManageUsers(): boolean {
    const allowed = [
      Permissions.ResetStudentPassword,
      Permissions.ManageSchoolStaffAccounts,
    ]
    if (
        (this.user.permissions as []).some((permission) =>
            allowed.includes(permission),
        )
    ) {
      return true
    }
    return false
  }

  private get hasAccessToResetStudentPassword(): boolean {
    const allowed = [Permissions.ResetStudentPassword]
    if (
        (this.user.permissions as []).some((permission) =>
            allowed.includes(permission),
        )
    ) {
      return true
    }
    return false
  }

  private get hasAccessToModifyStudentWifi(): boolean {
    const allowed = [Permissions.ModifyStudentWifi]
    if (
        (this.user.permissions as []).some((permission) =>
            allowed.includes(permission),
        )
    ) {
      return true
    }
    return false
  }

  private get hasAccessToChangeStatusOfStudents(): boolean {
    const allowed = [Permissions.ChangeStatusOfStudents]
    if ((this.user.permissions as []).some((permission) => allowed.includes(permission),)) {
      return true
    }
    return false
  }

  private get isUsersFilled(): boolean {
    return this.users.length <= 0
  }

  private get hasAccessToManageSchoolStaffAccounts(): boolean {
    const allowed = [Permissions.ManageSchoolStaffAccounts]
    if (
        (this.user.permissions as []).some((permission) =>
            allowed.includes(permission),
        )
    ) {
      return true
    }
    return false
  }

  private get hasAccessToChangeEmail(): boolean {
    const allowed = [Permissions.ChangeEmail]
    if (
        (this.user.permissions as []).some((permission) =>
            allowed.includes(permission),
        )
    ) {
      return true
    }
    return false
  }

  private get hasAccessToCSVExport(): boolean {
    const allowed = [Permissions.ExportAccountsToCsv]
    if (
        (this.user.permissions as []).some((permission) =>
            allowed.includes(permission),
        )
    ) {
      return true
    }
    return false
  }

  private get hasAccessToGenerateLabels(): boolean {
    const allowed = [Permissions.GenerateLabels]
    return this.user.permissions.some((permission) => allowed.includes(permission))
  }

  private get hasAccessToResetYear(): boolean {
    const allowed = [Permissions.ResetYearStudentsPassword]
    if (
        (this.user.permissions as []).some((permission) =>
            allowed.includes(permission),
        )
    ) {
      return true
    }
    return false
  }

  private hasAccessToManageThisUser(user: User) {
    if (user.fonctions === 'Élève') {
      return this.hasAccessToModifyStudentWifi
    } else {
      return this.hasAccessToManageSchoolStaffAccounts
    }
  }

  private get filterSelected() {
    const filters = ['selectedFunctions', 'selectedYears', 'selectedClasses', 'selectedWifiStatus', 'selectedADEnabled']
    return filters.filter( (filter) => this.$data[filter] !== '' && this.$data[filter].length !== 0 && this.$data[filter] !== null)
  }

  private resetFilter(item) {
    this.$data[item] = []
    this.filterLogical()
  }

  private userSelected(payload: any) {
      payload.items.forEach( (item) => {
        if (payload.value) {
          const index = this.selectedUsers.findIndex((user) => user.ad_personne_id === item.ad_personne_id)
          if (index === -1) {
            this.selectedUsers.push(item)
          }
        } else {
        const index = this.selectedUsers.findIndex((user) => user.ad_personne_id === item.ad_personne_id)
        this.selectedUsers.splice(index, 1)
        }
      })
  }

  private async search(filter: string | null) {
    if (filter && filter.length >= 4) {
      this.isPending = true
      try {
        this.users = await UserService.search(filter)
        for (const user of this.users) {
          this.populateData(user)
        }
        this.sortData()
        this.filterLogical()
      } catch (error: any) {
        if (error.response !== undefined) {
          this.errorHandlerService.handle(this, error.response.data.code, true)
        }
      } finally {
        this.isPending = false
      }
    }
  }

  private getEstablishments() {
    this.isPending = true

    this.establishments = Object.assign([], this.user.schools)
    sortStringValues(this.establishments, 'nom')
    this.isPending = false
    if (this.establishments.length === 1) {
      const schoolID = this.establishments[0].etablissement_id
      this.onSelectedEstablishmentIdChanged(schoolID, '')
    }
  }

  private async getUsers(establishmentId: string) {
    this.isPending = true
    try {
      this.selectedEstablishmentId = establishmentId
      this.users = await UserService.getUsersFromEstablishment(establishmentId)
      this.addCurrentSchoolData(this.users, establishmentId)
      this.filterLogical()
    } catch (error: any) {
      this.errorHandlerService.handle(this, error.response.data.code, true)
    } finally {
      this.isPending = false
    }
  }

  private addCurrentSchoolData(users: User[], establishmentId: string) {
    const establishment = this.establishments.find(
        (e) => e.etablissement_id === establishmentId,
    )
    if (establishment) {
      this.selectedSchoolName = establishment.nom
      for (const user of users) {
        this.populateData(user)
      }
      this.sortData()
      if (this.user.classes.length > 0) {
        this.classes.unshift('Mes classes')
      }
    }
  }

  private populateData(user: User) {
    user.wifiCSV = user.wifi ? 'Oui' : 'Non'
    this.populateUserFunctions(user)
    this.populateUserYears(user)
    this.populateUserClasses(user)
  }

  private sortData() {
    sortStringValues(this.functions)
    sortStringValues(this.years)
    sortStringValues(this.classes)
  }

  private populateUserFunctions(user: User) {
    if (user.fonctions) {
      const userFunctions = user.fonctions.split(',')

      const capitalizedUserFonctions: string[] = []
      userFunctions.forEach((userFunction) => {
        const capitalizedUserFonction = this.capitalize(userFunction)
        capitalizedUserFonctions.push(capitalizedUserFonction)
        if (!this.functions.includes(capitalizedUserFonction)) {
          this.functions.push(capitalizedUserFonction)
        }
      })
      user.fonctions = capitalizedUserFonctions.join(',')
    }
  }

  private populateUserClasses(user: User) {
    if (user.classes) {
      user.classes.forEach((userClass) => {
        if (userClass !== '') {
          if (!this.classes.includes(userClass)) {
            this.classes.push(userClass)
          }
        } else {
          const noClass = 'Aucune'
          user.classes = [noClass]
          if (!this.classes.includes(noClass)) {
            this.classes.push(noClass)
          }
        }
      })
    }
  }

  private populateUserYears(user: User) {
    if (user.years) {
      user.years.forEach((userYear) => {
        if (userYear !== '') {
          if (!this.years.includes(userYear)) {
            this.years.push(userYear)
          }
        } else {
          const noYear = 'Aucune'
          user.years = [noYear]
          if (!this.years.includes(noYear)) {
            this.years.push(noYear)
          }
        }
      })
    }
  }

  private async onSelectedEstablishmentIdChanged(
      establishmentId: string,
      oldEstablishmentId: string,
  ) {
    this.selectedEstablishmentId = establishmentId
    this.basicSearch = ''
    if (!establishmentId) {
        this.clearDataTable()
        return
    }
    if (establishmentId) {
      this.clearDataTable()
      await this.getUsers(establishmentId)
    }
  }

  private filterLogical() {
    this.$emit('update:page', this.page)
    this.selectedUsers = []
    this.filteredUsers = this.users.filter((user) => {
      if (Array.isArray(this.selectedFunctions) && this.selectedFunctions.length !== 0) {
        const hasSameFunction = this.selectedFunctions.some((fonction: string) => user.fonctions.includes(fonction))
        if (hasSameFunction === false) {
          return false
        }
      }
      if (Array.isArray(this.selectedYears) && this.selectedYears.length !== 0) {
        if (user.years.length === 0) {
          return false
        }
        const hasSameYear = user.years.some((year: string) => this.selectedYears.includes(year))
        if (hasSameYear === false) {
          return false
        }
      }
      if (Array.isArray(this.selectedClasses) && this.selectedClasses.length !== 0) {
        if (user.classes.length === 0) {
          return false
        }
        if (user.classes.some((classe: string) => this.selectedClasses.includes(classe)) === false
          ) {
            return false
          }
      }
      if (this.selectedWifiStatus) {
        if (this.selectedWifiStatus === 'Activé' && user.wifi === false) {
          return false
        } else if (
            this.selectedWifiStatus === 'Désactivé' &&
            user.wifi === true
        ) {
          return false
        }
      }
      if (this.selectedADEnabled) {
        if (this.selectedADEnabled === 'Activé' && user.ad_est_actif === false) {
          return false
        } else if (
            this.selectedADEnabled === 'Désactivé' &&
            user.ad_est_actif === true
        ) {
          return false
        }
      }
      return true
    })
    this.sortData()
  }

  private get hasSameClass() {
    if (this.selectedUsers.length >= 2) {
      const firstClass = this.selectedUsers[0].classes.join()
      return this.selectedUsers.some(
          (user) => user.classes.join() !== firstClass,
      )
    }
    return true
  }

  private get hasOwnedClassSelected() {
    if (this.selectedUsers.length > 0 ) {
      return this.selectedUsers.every((user) => this.user.classes.includes(user.classes.join()) && user.fonctions === 'Élève')
    } else {
      return false
    }
  }

  private get hasUsersToFilter() {
    return this.users.length > 0
  }

  private get hasSameYearAndIsStudent() {
    if (this.selectedUsers.length >= 2) {
      const firstYear = this.selectedUsers[0].years.join()
      return this.selectedUsers.every((user) => user.years.join() === firstYear && user.fonctions === 'Élève')
    }
    return false
  }

  private hasUsersSelected() {
    return this.selectedUsers.length > 0 ? true : false
  }

  private clearSearch() {
    this.clearDataTable()
  }

  private clearDataTable() {
    this.clearFiltersSelection()
    this.clearFiltersData()
    this.users = []
    this.selectedEstablishmentId = ''
    this.selectedSchoolName = ''
  }

  private clearFiltersSelection() {
    this.selectedYears = []
    this.selectedClasses = []
    this.selectedFunctions = []
    this.selectedWifiStatus = ''
    this.selectedADEnabled = ''
  }

  private clearFiltersData() {
    this.filteredUsers = []
    this.selectedUsers = []
    this.functions = []
    this.years = []
    this.classes = []
  }

  private resetFilterDialog() {
    this.clearFiltersSelection()
    this.filterLogical()
  }

  private get usersToExport() {
    return this.selectedUsers.length > 0
        ? this.selectedUsers
        : this.filteredUsers
  }

  private async resetPassword(user: User) {
    this.isPending = true
    const username = user.identifiant
    const school = user.etablissement_id
    const userType = user.fonctions === 'Élève' ? 'student' : 'schoolStaff'
    try {
      const response = await UserService.resetUserPassword(
          user,
          school,
          userType,
      )
      if (userType === 'student') {
        user.password = response['password']
        user.hasResetPassword = true
        this.$notificationService.success(
            this.$t('USER.ResetPasswordSucceedStudent').toString() + username,
        )
      } else {
        this.$notificationService.success(
            this.$t('USER.ResetPasswordSucceedNotStudent').toString() +
            username +
            this.$t('USER.ResetPasswordSucceedNotStudent2').toString() +
            response['email'],
        )
      }
    } catch (error: any) {
      this.errorHandlerService.handle(this, error.response.data.code, true)
    } finally {
      this.isPending = false
    }
  }

  private async resetUsersPassword(users: User[], sameYear: boolean) {
    this.isPending = true
    const usernames: string[] = []
    let schoolName: string = ''
    let userType: 'student' | 'schoolStaff' = 'student'

    for (const user of users) {
      userType = user.fonctions === 'Élève' ? 'student' : 'schoolStaff'
      if (schoolName === '') {
        schoolName = user.etablissement_id
      }
      usernames.push(user.identifiant)
    }

    try {
      let response: any
      if (sameYear) {
        response = await UserService.resetStudentsYearPassword(
            users,
            schoolName,
        )
      } else {
        response = await UserService.resetUsersPassword(
            users,
            schoolName,
            userType,
        )
      }

      for (const user of response.users) {
        const index = this.filteredUsers.findIndex(
            (filteredUser) => filteredUser.identifiant === user.identifiant,
        )
        if (userType === 'student') {
          this.filteredUsers[index].password = user.password
          this.filteredUsers[index].hasResetPassword = true
        }
      }
      this.$notificationService.success(
          this.$t('USER.ResetPasswordsSucceed').toString() +
          response.users.map((user: any) => user.identifiant).join(', '),
      )

      const errorCodeKey = 'code'
      const failedUsersGroupedByErrorCode = response.failUsers.reduce(
          (rv: any, x: any) => {
            (rv[x[errorCodeKey]] = rv[x[errorCodeKey]] || []).push(x)
            return rv
          },
          {},
      )

      for (const errorCode of Object.keys(failedUsersGroupedByErrorCode)) {
        const failedUsers = failedUsersGroupedByErrorCode[errorCode]
        const errorMessage = this.errorHandlerService.handle(
            this,
            errorCode,
            false,
        )
        this.$notificationService.error(
            errorMessage +
            ': ' +
            failedUsers.map((user: any) => user.identifiant).join(', '),
        )
      }
    } catch (error: any) {
      this.errorHandlerService.handle(this, error.response.data.code, true)
    } finally {
      this.isPending = false
    }
    this.isPending = false
  }

  private getSelectedRowsCount() {
    return this.selectedUsers.length
  }

  private initialize() {
    this.getEstablishments()
    this.createHeaders()
  }

  private createHeaders() {
    if (this.user.schools.length > 1) {
      this.headers.push(
          new GridHeader(
              this.$t('Establishment').toString(),
              'etablissements',
              'left',
              true,
              100,
          ),
      )
    }
    this.headers.push(
        new GridHeader(this.$t('Name').toString(), 'nom', 'left', true, 100),
    )
    this.headers.push(
        new GridHeader(
            this.$t('FirstName').toString(),
            'prenom',
            'left',
            true,
            100,
        ),
    )
    this.headers.push(
        new GridHeader(
            this.$t('Username').toString(),
            'identifiant',
            'left',
            true,
            100,
        ),
    )
    this.headers.push(
        new GridHeader(this.$t('Email').toString(), 'courriel', 'left', true, 100),
    )
    this.headers.push(
        new GridHeader(
            this.$t('Function').toString(),
            'fonctions',
            'left',
            true,
            100,
        ),
    )
    this.headers.push(
        new GridHeader(this.$t('USER.Year').toString(), 'years', 'left', true, 75),
    )
    this.headers.push(
        new GridHeader(
            this.$t('USER.Grade').toString(),
            'classes',
            'left',
            true,
            75,
        ),
    )
    this.headers.push(
        new GridHeader(
            this.$t('AccountStatus').toString(),
            'ad_est_actif',
            'center',
            true,
            25,
        ),
    )
    this.headers.push(
        new GridHeader(
            this.$t('Actions').toString(),
            'actions',
            'left fixed',
            false,
            '5%',
            'fixed',
        ),
    )
  }

  private handleDialogClose() {
    this.onSelectedEstablishmentIdChanged(this.selectedEstablishmentId, '')
  }

  private showChangeEmailDialog(user: User) {
    this.changeUserEmail = user
    this.changeEmailDialog = true
  }

  private usersChangeStatus(user: User) {
    this.$refs.usersChangeStatusDialog.show(user)
  }

  private async saveMailChange(chosenEmail: string, username: string) {
    await UserService.updateMask(chosenEmail, username)
    this.onSelectedEstablishmentIdChanged(this.selectedEstablishmentId, '')
  }

  private closeMailChangeDialog() {
    this.changeEmailDialog = false
  }

}
